import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Typography,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { updateScreenTitle } from "../../../LayoutContainers/AppLayout/redux/AppSlice";
import WDBox from "../../../MaterialComponents/WDBox";
import AppService from "../../../Configurations/Appservice";
import { CheckCircle } from "@mui/icons-material";

const PaymentHistory = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(updateScreenTitle("Payment History"));
    return () => dispatch(updateScreenTitle(""));
  }, []);

  useEffect(() => {
    fetchPaymentHistory();
  }, []);

  const fetchPaymentHistory = async () => {
    try {
      setLoading(true);
      const authInfo = localStorage.getItem("authInfo");
      const parsedAuth = JSON.parse(authInfo);
      const partnerId = parsedAuth.principalInfo.organisationId;

      const response = await AppService.get(
        `api/channel-partners/${partnerId}/commission/payment-history`
      );
      setPayments(response || []);
      setError(null);
    } catch (err) {
      console.error("Error fetching payment history:", err);
      // Use mock data
      const mockPayments = [
        { period: "October 2025", amount: "185.00", status: "PAID", paymentDate: "2025-10-31", referenceNumber: "BT-2025-10-001" },
        { period: "September 2025", amount: "175.00", status: "PAID", paymentDate: "2025-09-30", referenceNumber: "BT-2025-09-001" },
        { period: "August 2025", amount: "160.00", status: "PAID", paymentDate: "2025-08-30", referenceNumber: "BT-2025-08-001" },
        { period: "July 2025", amount: "145.00", status: "PAID", paymentDate: "2025-07-31", referenceNumber: "BT-2025-07-001" },
        { period: "June 2025", amount: "135.00", status: "PAID", paymentDate: "2025-06-30", referenceNumber: "BT-2025-06-001" },
        { period: "May 2025", amount: "95.00", status: "PAID", paymentDate: "2025-05-31", referenceNumber: "BT-2025-05-001" },
      ];
      setPayments(mockPayments);
      setError(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const totalPaid = payments.reduce((sum, payment) =>
    sum + parseFloat(payment.amount || 0), 0
  );

  return (
    <WDBox>
      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: "#e0e0e0" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold", color: "#000", fontSize: "14px", padding: "12px" }}>Period</TableCell>
              <TableCell align="right" sx={{ fontWeight: "bold", color: "#000", fontSize: "14px", padding: "12px" }}>
                Amount
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: "bold", color: "#000", fontSize: "14px", padding: "12px" }}>
                Status
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "#000", fontSize: "14px", padding: "12px" }}>Payment Date</TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "#000", fontSize: "14px", padding: "12px" }}>Reference Number</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                  <Alert severity="info">No payment history available</Alert>
                </TableCell>
              </TableRow>
            ) : (
              payments.map((payment, index) => (
                <TableRow key={index} hover>
                  <TableCell sx={{ fontWeight: "500" }}>{payment.period}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold", color: "#2ecc71" }}>
                    AED {parseFloat(payment.amount).toFixed(2)}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      icon={<CheckCircle />}
                      label={payment.status}
                      color={payment.status === "PAID" ? "success" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{payment.paymentDate}</TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: "monospace", background: "#f5f5f5", p: 0.5, borderRadius: 0.5, display: "inline-block" }}>
                      {payment.referenceNumber}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {payments.length > 0 && (
        <Box sx={{ mt: 3, p: 2, background: "#f9f9f9", borderRadius: 1 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
            <div>
              <strong>Total Payments:</strong> {payments.length}
            </div>
            <div>
              <strong>Total Amount Paid:</strong> AED {totalPaid.toFixed(2)}
            </div>
            <div>
              <strong>Average Payment:</strong> AED {(totalPaid / payments.length).toFixed(2)}
            </div>
            <div>
              <strong>Status:</strong>{" "}
              <Chip
                label="All Payments Completed"
                color="success"
                size="small"
                icon={<CheckCircle />}
              />
            </div>
          </div>
        </Box>
      )}
    </WDBox>
  );
};

export default PaymentHistory;
